import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Factory, Crown, ArrowUpRight } from "lucide-react";
import { RegisterLayout } from "../components/RegisterLayout";
import { BUYER_BADGES } from "../lib/badges";

export default function RegisterHub() {
  const cards = [
    {
      to: "/register/buyer",
      testid: "choose-buyer",
      Icon: ShoppingCart,
      title: "Register as a Buyer",
      desc: "Source cement & steel in bulk. Choose your partner tier — Super-Stockist, Wholesaler, Dealer or Booking Agent.",
      accent: "#B87333",
    },
    {
      to: "/register/seller",
      testid: "choose-seller",
      Icon: Factory,
      title: "Register as a Seller",
      desc: "Apply to become an authorised supplier for Vinci Inc. Complete the critical profiling form for review.",
      accent: "#2FB999",
    },
    {
      to: "/director/apply",
      testid: "choose-director",
      Icon: Crown,
      title: "Apply for the Founders Suite",
      desc: "Level-0 Executive Board access. Presidential visibility over the Master Ledger, Big-Cap League and 20-state supplier grid. ₹2,50,000 one-time authorization fee · Admin-only approval.",
      accent: "#C9A961",
      exclusive: true,
    },
  ];

  return (
    <RegisterLayout
      kicker="/ Join the Network"
      title="Create your account"
      subtitle="Vinci Inc. onboards verified buyers and authorised sellers. Select how you'd like to work with us."
    >
      <div className="grid md:grid-cols-3 gap-6">
        {cards.map((c, i) => (
          <motion.div
            key={c.to}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to={c.to}
              data-testid={c.testid}
              className={`group block h-full rounded-sm border p-8 md:p-10 transition-colors relative overflow-hidden ${
                c.exclusive ? "border-[#C9A961]/40 hover:border-[#C9A961]" : "border-white/10 hover:border-white/30"
              }`}
            >
              <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full blur-3xl opacity-20 transition-opacity group-hover:opacity-40" style={{ background: c.accent }} />
              <div className="relative z-10">
                {c.exclusive && (
                  <span className="absolute right-0 top-0 font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#C9A961]">
                    · Level 0 · Executive ·
                  </span>
                )}
                <span className="grid place-items-center w-12 h-12 rounded-full border" style={{ borderColor: `${c.accent}66`, color: c.accent }}>
                  <c.Icon size={20} />
                </span>
                <h2 className="font-serif text-3xl md:text-4xl mt-8">{c.title}</h2>
                <p className="font-body text-[#8A8A82] mt-4 leading-relaxed">{c.desc}</p>
                <span className="inline-flex items-center gap-2 mt-8 font-grotesk text-xs uppercase tracking-[0.2em]" style={{ color: c.accent }}>
                  Continue <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-14 flex flex-wrap gap-3">
        {Object.values(BUYER_BADGES).map((b) => (
          <span key={b.key} className="inline-flex items-center gap-2 rounded-full border px-4 py-2" style={{ borderColor: `${b.glow}44` }}>
            <span className="grid place-items-center w-6 h-6 rounded-full text-black" style={{ background: `linear-gradient(135deg, ${b.from}, ${b.to})` }}>
              <b.Icon size={13} />
            </span>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.18em]" style={{ color: b.to }}>{b.label}</span>
          </span>
        ))}
      </div>

      <p className="mt-10 font-body text-sm text-[#8A8A82]">
        Already registered? <Link to="/login" className="text-[#B87333] hover:underline">Sign in</Link>
      </p>
    </RegisterLayout>
  );
}
